import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Trash2, ShieldCheck, User as UserIcon } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", email: "", password: "", role: "technician" };

export default function Users() {
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [delTarget, setDelTarget] = useState(null);

  const load = () => api.users().then(setUsers).catch(() => setUsers([]));
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const create = async () => {
    if (!form.email.trim() || !form.password) { toast.error("Email and password required"); return; }
    setBusy(true);
    try { await api.createUser(form); toast.success("User created"); setOpen(false); setForm(EMPTY); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const changeRole = async (u, role) => {
    try { await api.updateUserRole(u.id, role); toast.success(`${u.name || u.email} is now ${role}`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    const t = delTarget; setDelTarget(null);
    try { await api.deleteUser(t.id); toast.success("User removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 sm:px-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Team</span>
          <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">User management</h1>
          <p className="mt-1 text-sm text-muted-foreground">Admins manage everything; technicians can log surveys, assets, faults and maintenance.</p>
        </div>
        <Button data-testid="new-user-btn" onClick={() => { setForm(EMPTY); setOpen(true); }} className="gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90"><Plus className="h-4 w-4" /> Add user</Button>
      </div>

      {users === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : (
        <div className="space-y-2">
          {users.map((u) => (
            <div key={u.id} data-testid={`user-row-${u.id}`} className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary text-foreground">
                {u.role === "admin" ? <ShieldCheck className="h-5 w-5 text-primary" /> : <UserIcon className="h-5 w-5 text-muted-foreground" />}
              </span>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-foreground">{u.name || u.email.split("@")[0]}{u.id === user?.id && <span className="ml-2 text-xs text-muted-foreground">(you)</span>}</div>
                <div className="truncate text-xs text-muted-foreground">{u.email}</div>
              </div>
              <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={u.id === user?.id}>
                <SelectTrigger data-testid={`user-role-${u.id}`} className="h-9 w-36 bg-secondary/40 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="technician">Technician</SelectItem>
                </SelectContent>
              </Select>
              <Button data-testid={`user-del-${u.id}`} variant="ghost" size="icon" disabled={u.id === user?.id} onClick={() => setDelTarget(u)} className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive disabled:opacity-30"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Add user</DialogTitle><DialogDescription>Create an account and assign a role.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label className="mb-1.5 block text-xs text-muted-foreground">Name</Label><Input data-testid="user-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-secondary/40" /></div>
            <div><Label className="mb-1.5 block text-xs text-muted-foreground">Email</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-secondary/40" /></div>
            <div><Label className="mb-1.5 block text-xs text-muted-foreground">Temporary password</Label><Input data-testid="user-password" type="text" value={form.password} onChange={(e) => set("password", e.target.value)} className="bg-secondary/40" /></div>
            <div><Label className="mb-1.5 block text-xs text-muted-foreground">Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}><SelectTrigger data-testid="user-role-select" className="bg-secondary/40"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="admin">Admin</SelectItem><SelectItem value="technician">Technician</SelectItem></SelectContent></Select>
            </div>
            <Button data-testid="create-user-confirm" onClick={create} disabled={busy} className="w-full gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Create user</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Remove this user?</AlertDialogTitle><AlertDialogDescription>{delTarget?.email} will lose access immediately.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-user" onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
